import { Box, ClickAwayListener, Fade, Grid, Icon, Paper, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import React, { useState } from 'react';
import {
  AttachMoney,
  Close,
  Edit,
  Engineering,
  Event,
  HourglassEmpty,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Loop,
  Numbers,
  People,
  Percent,
  PriorityHigh,
  Schedule,
  ShoppingCart,
  Subject,
  TableRestaurant,
  Title,
  WhereToVote,
} from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import './OrderBoxPopper.scss';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import TimeUtils from '../../utils/TimeUtils';
import { useCalendar } from '../CalendarContext/CalendarContext';
import ResponsivePopper from '../../common/ui/ResponsivePopper';
import { useAuth } from '../../session/InternalAuthProvider';
import { getOrderPage } from '../../utils/AccountUtils';
import { localizedLabel } from '../../utils/maskUtils';
import { useLanguage } from '../../common/GeneralUtils';
import { AccountData, BookingType, CustomerChoosableResourceAllocationData, OrderData } from '../../api';
import { WalkInChip } from '../../common/WalkInChip';
import { UseAuthPermissions } from '../../session/UseAuthPermissions';
import { whatsAppLinkFormatter } from '../../common/whatsAppLinkFormatter';
import { getEmployeeNames } from '../CalendarUtils';
import CopyToClipboardButton from '../dialogs/CopyToClipboardButton';
import { usePriceRenderer } from '../../common/usePriceRenderer';
import { WorkshopCircle } from '../../common/ui/WorkshopCircle';
import { TicketBreakdownDisplay } from '../../editOrderUiObjects/TicketBreakdownDisplay';
import { getTimeFormat } from '../../common/getDateAndTimeFormat';
import { isPendingHoldExpired, isPendingOrder } from '../../common/pendingOrderUtils';
import { OrderActionsBar } from './OrderActionsBar';

type OrderBoxPopperProps = {
  anchorEl: any;
  closeOrderBoxPopper: () => void;
  order: OrderData;
  getNextOrder: () => OrderData | undefined;
  getPreviousOrder: () => OrderData | undefined;
  openOrder: (event: any, order: OrderData) => void;
};
const OrderBoxPopper = ({
  anchorEl,
  closeOrderBoxPopper,
  order,
  getNextOrder,
  getPreviousOrder,
  openOrder,
}: OrderBoxPopperProps) => {
  const theme = useTheme();
  const { isAtLeastSupervisor } = UseAuthPermissions();
  // in order to allow live updates on checking as order is a saparate use state and not part of useOrders its not
  // getting updated. a bit hacky solution but we will live with it for now
  const [internalOrder, setInternalOrder] = useState(order);
  const open = Boolean(anchorEl);
  const { t } = useTranslation();
  const { openEditOrderDrawer, users } = useCalendar();

  // @ts-ignore
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const timeFormat = getTimeFormat(account);

  const language = useLanguage();
  const orderPage = getOrderPage(account, internalOrder.order_page_id);
  const { SlimPrice } = usePriceRenderer({
    orderOrVoucher: internalOrder,
    initialPrice: internalOrder.price,
    returningCustomerFromOrderId: internalOrder.returning_customer_from_order_id,
    extraPaymentLines: internalOrder.extra_payment_lines,
  });
  const element = document.querySelector('#calendar-container');

  const formatEmployees = () => {
    if (internalOrder.employee_ids.length) {
      return getEmployeeNames(internalOrder.employee_ids, users).map((x) => (
        <Grid key={`employee-${x}`} item color="text.primary">
          {x}
        </Grid>
      ));
    }
    return '';
  };
  const nextOrder = getNextOrder();
  const previousOrder = getPreviousOrder();

  return (
    <ResponsivePopper
      open={open}
      anchorEl={anchorEl}
      placement="left-start"
      modifiers={[
        {
          name: 'flip',
          enabled: true,
          options: {
            boundary: element,
            fallbackPlacements: ['left-start', 'left-end', 'right-start', 'right-end', 'top', 'bottom'],
          },
        },
        {
          name: 'preventOverflow',
          options: {
            altAxis: true,
            padding: 10,
          },
        },
      ]}>
      <Fade in timeout={520}>
        <div className="height-100">
          <ClickAwayListener onClickAway={closeOrderBoxPopper}>
            <Paper elevation={24} className="order-box-popper-content height-100">
              <Stack direction="row" justifyContent="flex-end" padding={1}>
                <Grid
                  container
                  flexDirection={language === 'he' ? 'row-reverse' : 'row-reverse'}
                  justifyContent={'flex-end'}>
                  <Tooltip title={t('next')} arrow>
                    <span>
                      <IconButton disabled={!nextOrder} onClick={() => openOrder(undefined, nextOrder!)}>
                        {language === 'he' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t('previous')} arrow>
                    <span>
                      <IconButton disabled={!previousOrder} onClick={() => openOrder(undefined, previousOrder!)}>
                        {language === 'he' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid>
                <OrderActionsBar
                  order={internalOrder}
                  onAfterAction={closeOrderBoxPopper}
                  onOrderUpdated={setInternalOrder}
                />
                <Tooltip title={!isAtLeastSupervisor ? t('noPermissions') : t('edit')} arrow>
                  <span>
                    <IconButton
                      disabled={!isAtLeastSupervisor}
                      onClick={() => {
                        openEditOrderDrawer({ order: internalOrder });
                        closeOrderBoxPopper();
                      }}>
                      <Edit />
                    </IconButton>
                  </span>
                </Tooltip>
                <IconButton onClick={closeOrderBoxPopper}>
                  <Tooltip title={t('close')} arrow>
                    <Close />
                  </Tooltip>
                </IconButton>
              </Stack>

              <Box className="details-container">
                <Box className="detail-container">
                  <WorkshopCircle account={account} orderPageId={order.order_page_id} language={language} />
                  <Box className="name-container" alignItems="center" display="flex">
                    {!!internalOrder.returning_customer && (
                      <Tooltip title={t('returnningCustomer')} arrow>
                        <Grid container alignItems="center" width="auto" sx={{ color: 'primary.main' }} mx={1}>
                          <Loop />
                          {internalOrder.returning_customer > 1 && (
                            <span style={{ fontSize: '14px', fontWeight: 600 }}>
                              {internalOrder.returning_customer}
                            </span>
                          )}
                        </Grid>
                      </Tooltip>
                    )}
                    {`${internalOrder.firstname} ${internalOrder.lastname}`}
                  </Box>
                </Box>

                <Box className="date-container date-container-workshop">
                  {moment.unix(internalOrder.timestamp || 0).format('dddd, MMMM, DD')}
                  &nbsp;&nbsp;&nbsp;⋅&nbsp;&nbsp;&nbsp;
                  {moment
                    .unix(internalOrder.time || 0)
                    .tz('utc')
                    .format(timeFormat)}{' '}
                  -{' '}
                  {moment
                    .unix((internalOrder.time || 0) + internalOrder.duration)
                    .tz('utc')
                    .format(timeFormat)}
                </Box>

                <Box className="detail-container">
                  <div className="icon-container">
                    <Title />
                  </div>
                  <Box>{orderPage.label[language]}</Box>
                </Box>
                <Box className="detail-container">
                  <div className="icon-container">
                    <PriorityHigh sx={{ color: 'warning.main' }} />
                  </div>
                  <Tooltip title={t('comment')} placement="top" arrow>
                    <Box maxWidth="300px">
                      <span>{internalOrder.comment}</span>
                    </Box>
                  </Tooltip>
                </Box>
                <Box className="detail-container">
                  <div className="icon-container">
                    <Engineering />
                  </div>
                  <Tooltip title={t('employees')} placement="top" arrow>
                    <Grid container item justifyContent="flex-start" flexWrap="nowrap" alignItems="center" gap={0.5}>
                      {formatEmployees()}
                    </Grid>
                  </Tooltip>
                </Box>
                {internalOrder.order_number != null && internalOrder.order_number !== 0 && (
                  <Box className="detail-container">
                    <div className="icon-container">
                      <Numbers />
                    </div>
                    <Tooltip title={t('order_number')} placement="top" arrow>
                      <Box>
                        <span>{t('order_number')}</span>
                        <span> &#35;</span>
                        <span>{internalOrder.order_number}</span>
                      </Box>
                    </Tooltip>
                  </Box>
                )}
                {internalOrder.booking_type === BookingType.WALK_IN && (
                  <Box className="detail-container">
                    <WalkInChip />
                  </Box>
                )}

                <Box className="detail-container">
                  <div className="icon-container">
                    <People />
                  </div>
                  <Box>
                    <Box>
                      {internalOrder.persons} {t('calendar.order_popper.persons')}
                    </Box>
                    <TicketBreakdownDisplay order={internalOrder} orderPage={orderPage} variant="stacked" />
                  </Box>
                </Box>

                <Box className="detail-container">
                  <div className="icon-container">
                    <Schedule />
                  </div>
                  <Tooltip title={t('calendar.order_popper.duration')} placement="top" arrow>
                    <Box>{TimeUtils.formatDuration(internalOrder.duration, t)}</Box>
                  </Tooltip>
                </Box>

                <Box className="detail-container">
                  <div className="icon-container">
                    <AttachMoney />
                  </div>
                  <SlimPrice />
                </Box>

                {internalOrder.custom_details &&
                  orderPage.custom_details.map((custom_detail: any, index: number) => {
                    return (
                      <Box key={index} className="detail-container">
                        <div className="icon-container">
                          {custom_detail.icon.endsWith('.svg') ? (
                            <img
                              src={custom_detail.icon}
                              alt=""
                              style={{
                                width: '20px',
                                height: '20px',
                              }}
                            />
                          ) : (
                            <Icon>{custom_detail.icon}</Icon>
                          )}
                        </div>
                        <Tooltip title={custom_detail.name} placement="top" arrow>
                          <Box>
                            <>
                              {custom_detail.type === 'str' && internalOrder.custom_details?.[custom_detail.name]}
                              {custom_detail.type === 'location' && (
                                <a
                                  className="link"
                                  target="_blank"
                                  href={`https://maps.google.com/?q=term=${
                                    internalOrder.custom_details?.[custom_detail.name]
                                  }`}
                                  rel="noreferrer">
                                  {internalOrder.custom_details?.[custom_detail.name] || null}
                                </a>
                              )}
                              {custom_detail.type === 'bool' && (
                                <div>
                                  {internalOrder.custom_details?.[custom_detail.name] ? (
                                    <span>{t('yes')}</span>
                                  ) : (
                                    <span>{t('no')}</span>
                                  )}
                                </div>
                              )}
                              {custom_detail.type === 'resourceInt' && (
                                <Typography>{internalOrder.custom_details?.[custom_detail.name]}</Typography>
                              )}
                            </>
                          </Box>
                        </Tooltip>
                      </Box>
                    );
                  })}

                {internalOrder.custom_personal_details &&
                  orderPage.custom_personal_details.map((custom_detail: any, index: number) => {
                    if (internalOrder.custom_personal_details?.[custom_detail.id]) {
                      return (
                        <Box key={index} className="detail-container">
                          <div className="icon-container">
                            <Icon>note</Icon>
                          </div>
                          <Tooltip title={custom_detail.label[language]} placement="top" arrow>
                            <Box>
                              {custom_detail.type === 'multiline' && (
                                <span>{internalOrder.custom_personal_details?.[custom_detail.id]}</span>
                              )}
                            </Box>
                          </Tooltip>
                        </Box>
                      );
                    }
                    return undefined;
                  })}

                {orderPage.shared_resources_allocations?.customer_choosable_resources?.map(
                  (allocation: CustomerChoosableResourceAllocationData) => {
                    const resource = account.shared_resources?.customer_choosable_resources?.find(
                      (r) => r.id === allocation.resource_id,
                    );
                    if (!resource) return null;

                    const chosenResource = internalOrder.chosen_resources?.find(
                      (cr) => cr.resource_id === allocation.resource_id,
                    );
                    const quantity = chosenResource?.quantity ?? allocation.default;

                    return (
                      <Box key={allocation.resource_id} className="detail-container">
                        <div className="icon-container">
                          <TableRestaurant />
                        </div>
                        <Tooltip title={resource.label[language]} placement="top" arrow>
                          <Box>
                            {resource.label[language]}: {quantity}
                          </Box>
                        </Tooltip>
                      </Box>
                    );
                  },
                )}

                {internalOrder.resource_option_id &&
                  (() => {
                    const resourceOption = account.shared_resources?.scheduler_configs
                      ?.flatMap((schedulerConfig) => schedulerConfig.allowed_masks ?? [])
                      .find((mask) => mask.order_page_id === internalOrder.order_page_id)
                      ?.resource_options?.find((option) => option.id === internalOrder.resource_option_id);
                    if (!resourceOption) return null;
                    const optionLabel = localizedLabel(resourceOption.label, language, resourceOption.id);
                    return (
                      <Box className="detail-container">
                        <div className="icon-container">
                          <TableRestaurant />
                        </div>
                        <Tooltip title={optionLabel} placement="top" arrow>
                          <Box>{optionLabel}</Box>
                        </Tooltip>
                      </Box>
                    );
                  })()}

                <Box className="detail-container multiple-details-container">
                  <div className="icon-container">
                    <Subject />
                  </div>
                  <Box className="multiple-details-container">
                    {t('calendar.order_popper.name')}: {internalOrder.firstname} {internalOrder.lastname}
                    <br />
                    <Grid container gap={1}>
                      {t('calendar.order_popper.email')}: {internalOrder.email}
                      <CopyToClipboardButton
                        width={'18px'}
                        color={theme.palette.text.secondary}
                        text={internalOrder.email}
                      />
                    </Grid>
                    <br />
                    <Grid container gap={1}>
                      {t('calendar.order_popper.phone')}:
                      <a
                        className="link"
                        target="_blank"
                        href={whatsAppLinkFormatter(internalOrder.phone, '')}
                        rel="noreferrer">
                        {internalOrder.phone}
                      </a>
                      <CopyToClipboardButton
                        width={'18px'}
                        color={theme.palette.text.secondary}
                        text={internalOrder.phone}
                      />
                    </Grid>
                  </Box>
                </Box>

                <Tooltip title={t('calendar.order_popper.creation_time')} placement="top" arrow>
                  <Box className="detail-container">
                    <div className="icon-container">
                      <Event />
                    </div>
                    <Box>
                      {TimeUtils.getLocalTime(internalOrder.created_at, account.timezone).format('dddd, MMMM, DD')}
                      &nbsp;&nbsp;&nbsp;⋅&nbsp;&nbsp;&nbsp;
                      {TimeUtils.getLocalTime(internalOrder.created_at, account.timezone).format(timeFormat)}
                    </Box>
                  </Box>
                </Tooltip>

                {account.returning_customer_settings && (
                  <Box className="detail-container">
                    <div className="icon-container">
                      <Percent />
                    </div>
                    <Grid container flexDirection="column">
                      <Typography>{t('calendar.order_popper.returningCsutomer')}</Typography>
                      <Grid container gap={1} alignItems="center">
                        {internalOrder.id}
                        <CopyToClipboardButton
                          width={'18px'}
                          color={theme.palette.text.secondary}
                          text={internalOrder.id}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}
                {internalOrder.checkin && (
                  <Box className="detail-container">
                    <div className="icon-container">
                      <WhereToVote />
                    </div>
                    <Tooltip title={t('calendar.order_popper.checkin')} placement="top" arrow>
                      <Box
                        sx={{
                          textTransform: 'capitalize',
                        }}>{`${internalOrder.checkin?.persons}/${internalOrder.persons}`}</Box>
                    </Tooltip>
                  </Box>
                )}

                <Box className="detail-container">
                  <div className="icon-container">
                    <ShoppingCart />
                  </div>
                  <Tooltip title={t('calendar.order_popper.status')} placement="top" arrow>
                    <Box sx={{ textTransform: 'capitalize' }}>
                      {t(`order_status.${internalOrder.status.toLowerCase()}`)}
                    </Box>
                  </Tooltip>
                </Box>

                {isPendingOrder(internalOrder) && internalOrder.pending_expires_at && (
                  <Tooltip title={t('calendar.order_popper.pendingHold')} placement="top" arrow>
                    <Box className="detail-container">
                      <div className="icon-container">
                        <HourglassEmpty />
                      </div>
                      <Box>
                        {isPendingHoldExpired(internalOrder)
                          ? t('calendar.order_popper.pendingHoldExpired')
                          : `${t('calendar.order_popper.pendingHoldUntil')} ${TimeUtils.getLocalTime(
                              internalOrder.pending_expires_at,
                              account.timezone,
                            ).format('DD/MM')} ⋅ ${TimeUtils.getLocalTime(
                              internalOrder.pending_expires_at,
                              account.timezone,
                            ).format(timeFormat)}`}
                      </Box>
                    </Box>
                  </Tooltip>
                )}
              </Box>
            </Paper>
          </ClickAwayListener>
        </div>
      </Fade>
    </ResponsivePopper>
  );
};

export default OrderBoxPopper;
